// src/private/src/pages/app/clients/components/DrilldownChart.jsx
import React, { memo, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const DRILLDOWN_METRICS = [
  { key: 'leads', label: 'Leads', color: '#2196f3' },
  { key: 'booked', label: 'Booked', color: '#7c3aed' },
  { key: 'issues', label: 'Issues', color: '#f59e0b' },
  { key: 'demos', label: 'Demos', color: '#f97316' },
  { key: 'netSold', label: 'Net Sold', color: '#22c55e' },
];

/**
 * Small bar chart shown inside vertical/channel drilldown panels.
 * Summarizes backend funnel metrics for the drilled-down row.
 */
function DrilldownChart({
  leads = 0,
  booked = 0,
  issues = 0,
  demos = 0,
  netSold = 0,
  title = '',
}) {
  const data = useMemo(() => [
    { name: 'Leads', value: leads, fill: '#2196f3' },
    { name: 'Booked', value: booked, fill: '#7c3aed' },
    { name: 'Issues', value: issues, fill: '#f59e0b' },
    { name: 'Demos', value: demos, fill: '#f97316' },
    { name: 'Net Sold', value: netSold, fill: '#22c55e' },
  ], [leads, booked, issues, demos, netSold]);

  const hasData = data.some((d) => d.value > 0);
  if (!hasData) return null;

  return (
    <Box sx={{ mb: 2 }}>
      {title && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
          {title}
        </Typography>
      )}
      <Box sx={{ width: '100%', height: 140 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 10 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 10 }}
              axisLine={false}
              tickLine={false}
              width={35}
            />
            <Tooltip
              contentStyle={{ fontSize: 12, borderRadius: 8 }}
              formatter={(value) => value.toLocaleString('en-US')}
            />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {data.map((entry) => (
                <Cell key={entry.name} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

export default memo(DrilldownChart);
